import axios from "../../../../../api/axios";
import { toast } from 'react-toastify';

/**
 * API endpoints for Snow & Ice Removal Log operations
 */
export const ENDPOINTS = {
  BASE: "/v2/snow-ice-removal-logs",
  CREATE: "/v2/snow-ice-removal-logs",
  GET_ALL: "/v2/snow-ice-removal-logs",
  GET_BY_ID: "/v2/snow-ice-removal-logs/:id",
  UPDATE: "/v2/snow-ice-removal-logs/:id",
  DELETE: "/v2/snow-ice-removal-logs/:id",
  SIGNED_URLS: "/v2/snow-ice-removal-logs/signed-urls",
  UPLOAD_REQUEST: "/v2/snow-ice-removal-logs/:id/upload-request",
  CONFIRM_UPLOAD: "/v2/snow-ice-removal-logs/:id/confirm-upload",
  DISTINCT_ACTIONS: "/v2/snow-ice-removal-logs/distinct-actions",
  DISTINCT_COMPLETED_BY: "/v2/snow-ice-removal-logs/distinct-completed-by",
  EXPORT_EXCEL: "/v2/snow-ice-removal-logs/export-excel",
  SEND_NOTIFICATION: "/v2/snow-ice-removal-logs/:id/send-notification",
};

/**
 * Centralized error handler for API calls
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Fallback message
 */
const handleApiError = (error, defaultMessage) => {
  const message =
    error.response?.data?.message ||
    error.message ||
    defaultMessage ||
    "An unexpected error occurred";
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

/**
 * Create a new Snow & Ice Removal Log
 * @param {Object} payload - Log data (date, time_started, time_completed, tasks, actions_performed, property_details, etc.)
 * @returns {Promise<Object>} Created log (enriched with completedByName)
 */
export const createSnowLog = async (payload) => {
  try {
    const response = await axios.post(ENDPOINTS.CREATE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to create snow/ice removal log");
  }
};

/**
 * Fetch all Snow & Ice Removal Logs with optional filters
 * @param {Object} [params={}] Query parameters
 * @param {string} [params.propertyId]
 * @param {string} [params.building_name]
 * @param {string} [params.startDate] YYYY-MM-DD
 * @param {string} [params.endDate] YYYY-MM-DD
 * @param {string} [params.actions] comma-separated actions
 * @param {string} [params.completedBy] ObjectId of who completed the log
 * @param {string} [params.completedRole] Role (Company, Manager, Vendor, TechnicalStaff)
 * @returns {Promise<{ success: boolean, count: number, data: Array, appliedFilters: Object }>}
 */
export const getSnowLogList = async (params = {}) => {
  try {
    const response = await axios.get(ENDPOINTS.GET_ALL, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch snow/ice removal logs");
  }
};

/**
 * Fetch distinct / unique values from actions_performed array
 * @returns {Promise<string[]>} Sorted array of unique action strings
 */
export const getDistinctActions = async () => {
  try {
    const response = await axios.get(ENDPOINTS.DISTINCT_ACTIONS);
    return response.data.data || [];
  } catch (error) {
    handleApiError(error, "Failed to fetch available action types");
    return [];
  }
};

/**
 * Fetch list of users who have completed (created/updated) snow logs
 * @returns {Promise<Array<{ role: string, object_id: string, name: string }>>}
 */
export const getDistinctCompletedBy = async () => {
  try {
    const response = await axios.get(ENDPOINTS.DISTINCT_COMPLETED_BY);
    return response.data.data || [];
  } catch (error) {
    handleApiError(error, "Failed to fetch users who completed snow logs");
    return [];
  }
};

/**
 * Fetch single Snow & Ice Removal Log by ID
 * @param {string} id Log _id
 * @returns {Promise<Object>} Enriched log object
 */
export const getSnowLogById = async (id) => {
  try {
    const url = ENDPOINTS.GET_BY_ID.replace(":id", id);
    const response = await axios.get(url);
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to fetch snow/ice removal log details");
  }
};

/**
 * Update existing Snow & Ice Removal Log
 * @param {string} id Log _id
 * @param {Object} payload Fields to update
 * @returns {Promise<Object>} Updated log (enriched)
 */
export const updateSnowLog = async (id, payload) => {
  try {
    const url = ENDPOINTS.UPDATE.replace(":id", id);
    const response = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to update snow/ice removal log");
  }
};

/**
 * Delete a Snow & Ice Removal Log
 * @param {string} id Log _id
 * @returns {Promise<Object>} Deletion result
 */
export const deleteSnowLog = async (id) => {
  try {
    const url = ENDPOINTS.DELETE.replace(":id", id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to delete snow/ice removal log");
  }
};

/**
 * Request presigned URL for direct upload to S3
 * @param {string} logId Log _id
 * @param {string} fileName Original filename
 * @param {string} contentType MIME type (image/jpeg, application/pdf, etc.)
 * @returns {Promise<{ presignedUrl: string, key: string, expectedContentType: string, expiresIn: number }>}
 */
export const requestUploadUrl = async (logId, fileName, contentType) => {
  try {
    const url = ENDPOINTS.UPLOAD_REQUEST.replace(":id", logId);
    const response = await axios.post(url, { fileName, contentType });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to obtain presigned upload URL");
  }
};

/**
 * Confirm successful upload and attach key to log attachments
 * @param {string} logId Log _id
 * @param {string} key S3 object key
 * @returns {Promise<{ key: string, attachments: string[] }>}
 */
export const confirmUpload = async (logId, key) => {
  try {
    const url = ENDPOINTS.CONFIRM_UPLOAD.replace(":id", logId);
    const response = await axios.post(url, { key });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to confirm attachment upload");
  }
};

/**
 * Get temporary signed URLs for viewing/downloading attachments
 * @param {string[]} keys Array of S3 keys
 * @returns {Promise<Record<string, string | null>>} key → signed URL (or null if failed)
 */
export const getAttachmentSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};

  try {
    const response = await axios.post(ENDPOINTS.SIGNED_URLS, { keys });
    return response.data.data;
  } catch (error) {
    handleApiError(error, "Failed to generate signed URLs for attachments");
    return {};
  }
};

/**
 * Export filtered logs to Excel (triggers browser download)
 * @param {Object} params Query filters
 * @param {string} params.propertyId **required**
 * @param {string} [params.building_name]
 * @param {string} [params.startDate]
 * @param {string} [params.endDate]
 * @returns {Promise<void>}
 */
export const exportSnowLogsToExcel = async (params = {}) => {
  if (!params.propertyId) {
    toast.error("Property ID is required to export logs");
    return;
  }

  try {
    toast.info("Generating Excel file...");

    const response = await axios.get(ENDPOINTS.EXPORT_EXCEL, {
      params,
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    let fileName = `Snow_Ice_Logs_${new Date().toISOString().split('T')[0]}.xlsx`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match?.[1]) fileName = match[1];
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success("Excel file downloaded");
  } catch (error) {
    handleApiError(error, "Failed to export logs to Excel");
  }
};

/**
 * Send email notification (with PDF + photos) for a log
 * Usually called after creation and all uploads are complete
 * @param {string} logId Log _id
 * @returns {Promise<{ success: boolean, message: string, data: { logId: string, attachmentsCount: number } }>}
 */
export const sendSnowLogNotification = async (logId) => {
  try {
    const url = ENDPOINTS.SEND_NOTIFICATION.replace(":id", logId);
    const response = await axios.post(url);
    toast.success("Notification sent successfully");
    return response.data;
  } catch (error) {
    handleApiError(error, "Failed to send snow/ice removal log notification");
  }
};